"use client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  authService,
  type SignUpCredentials,
  type SignInCredentials,
} from "@/services/supabase/auth";
import type { User, Session } from "@supabase/supabase-js";
import { useToast } from "@/hooks/use-toast";
import { useCookieAuth } from "@/hooks/useCookieAuth";

export const useAuth = () => {
  const { user, session, isLoading, signOut: cookieSignOut } = useCookieAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const signInWithEmail = useMutation({
    mutationFn: authService.signInWithEmail,
    onError: (error: Error) => {
      toast({
        title: "Sign in failed",
        description: error.message || "There was a problem signing in.",
        variant: "destructive",
      });
    },
  });

  const signUpWithEmail = useMutation({
    mutationFn: authService.signUpWithEmail,
    onError: (error: Error) => {
      toast({
        title: "Sign up failed",
        description:
          error.message || "There was a problem creating your account.",
        variant: "destructive",
      });
    },
  });

  const signOut = useMutation({
    mutationFn: async () => {
      // Use cookie-based sign out
      cookieSignOut();
      return Promise.resolve();
    },
    onSuccess: () => {
      // Clear all query cache on sign out
      queryClient.clear();
      queryClient.invalidateQueries({ queryKey: ["user"] });
      queryClient.invalidateQueries({ queryKey: ["profile"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Sign out failed",
        description: error.message || "There was a problem signing out.",
        variant: "destructive",
      });
    },
  });

  const resetPassword = useMutation({
    mutationFn: authService.resetPassword,
    onSuccess: () => {
      toast({
        title: "Password reset email sent",
        description: "Check your email for a password reset link.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Password reset failed",
        description:
          error.message || "There was a problem sending the reset email.",
        variant: "destructive",
      });
    },
  });

  const updatePassword = useMutation({
    mutationFn: authService.updatePassword,
    onSuccess: () => {
      toast({
        title: "Password updated",
        description: "Your password has been successfully updated.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Password update failed",
        description:
          error.message || "There was a problem updating your password.",
        variant: "destructive",
      });
    },
  });

  return {
    user,
    isLoading,
    isAuthenticated: !!user,
    signInWithEmail,
    signUpWithEmail,
    signOut,
    resetPassword,
    updatePassword,
    session,
  };
};

export function useAdminCheck(userId?: string) {
  return useQuery({
    queryKey: ["adminStatus", userId],
    queryFn: () => authService.isAdmin(userId!),
    enabled: !!userId,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

export function useSuperadminCheck(userId?: string) {
  return useQuery({
    queryKey: ["superadminStatus", userId],
    queryFn: () => authService.checkSuperadminStatus(userId!),
    enabled: !!userId,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}
